import axios from "axios";
import { emitReload } from "@/lib/reloadBus";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Arbeitszeit-Erfassung: nur Anfragen kurz nach echter Nutzer-Interaktion zählen als aktiv.
const ACTIVE_WINDOW_MS = 2 * 60 * 1000;
let lastInteraction = Date.now();
if (typeof window !== "undefined") {
  ["pointerdown", "keydown", "touchstart", "wheel", "scroll"].forEach((ev) =>
    window.addEventListener(ev, () => { lastInteraction = Date.now(); }, { passive: true, capture: true }));
}
const isUserActive = () =>
  document.visibilityState === "visible" && Date.now() - lastInteraction < ACTIVE_WINDOW_MS;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("novatus_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["X-User-Active"] = isUserActive() ? "1" : "0";
  // Multi-Tenancy: Super-Admin can pin all requests to a specific tenant
  // via the "active tenant" switcher in the Navbar. The header is silently
  // ignored by the backend for non-super-admin users.
  const actingTenant = localStorage.getItem("novatus_acting_tenant_id");
  if (actingTenant) {
    config.headers["X-Acting-Tenant-Id"] = actingTenant;
  }
  return config;
});

api.interceptors.response.use(
  (r) => {
    // Iter88zbu (2026-02-19) — Globaler Auto-Reload.
    // Nach jedem erfolgreichen mutating Request feuern wir einen ReloadBus-
    // Broadcast, sodass jede aktuell sichtbare Seite ihre Daten still
    // nachladen kann (via `useReloadBus('*', load)` oder eigenen Kanal).
    // Endpunkte, die keinen sichtbaren UI-Impact haben (Autofill-Historie,
    // Message-Read-Marker, Push-Subscriptions, Auth-Refresh) können via
    // `config.skipAutoReload = true` opt-out; wir setzen zusätzlich einen
    // URL-Blacklist-Fallback für die häufigsten Fälle.
    try {
      const cfg = r?.config || {};
      const method = (cfg.method || "").toLowerCase();
      const url = String(cfg.url || "");
      const isMutation = method === "post" || method === "patch" || method === "put" || method === "delete";
      const status = r?.status || 0;
      const blacklisted =
        cfg.skipAutoReload === true ||
        /^\/?autofill\//.test(url) ||
        /^\/?push\//.test(url) ||
        /^\/?auth\//.test(url) ||
        /messages\/mark-read/.test(url) ||
        /presence/.test(url);
      if (isMutation && status >= 200 && status < 300 && !blacklisted) {
        emitReload("*");
      }
    } catch { /* Bus darf niemals die Response-Chain brechen */ }
    return r;
  },
  (err) => {
    // Allow individual requests to opt out of the global "redirect to login on
    // 401" behaviour. Used by long-running media uploads (voice dictation,
    // installer photo upload) where a transient auth blip MUST NOT tear down
    // the entire React tree (which appears as a black screen on mobile).
    const skip401 = err?.config?.skip401Redirect === true;
    if (err.response?.status === 401 && !skip401) {
      localStorage.removeItem("novatus_token");
      localStorage.removeItem("novatus_user");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    // Normalize Pydantic 422 detail (which is an array of {type, loc, msg, input, url}
    // objects) into a flat human-readable string. Otherwise UI code that renders
    // `error.response.data.detail` directly crashes with
    // "Objects are not valid as a React child".
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) {
      err.response.data.detail = d
        .map((item) => {
          if (item && typeof item === "object") {
            const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== "body").join(".") : "";
            return loc ? `${loc}: ${item.msg || "ungültig"}` : (item.msg || "ungültiger Wert");
          }
          return String(item);
        })
        .join(" · ");
    } else if (d && typeof d === "object") {
      // Keep structured detail with a `code` field (used by the auth flow
      // banners for 404/403). Only flatten generic objects.
      if (!d.code) {
        err.response.data.detail = d.msg || JSON.stringify(d);
      }
    }
    return Promise.reject(err);
  }
);

export const fileUrl = (storagePath) => {
  const token = localStorage.getItem("novatus_token");
  return `${API}/files/${storagePath}?auth=${token}`;
};

// Nach Neu-Erzeugen am gleichen Pfad: eigener Cache-Schlüssel, damit Edge/Browser keine alte Version liefern.
export const freshFileUrl = (storagePath) => `${fileUrl(storagePath)}&v=${Date.now()}`;

export default api;
